import { inject as historyInject } from '@angular/core';
import { selectRealEstateState as selectHistoryState } from '../../store/selectors/real-estate.selectors';
import { MapListingCards } from '../../services/map-listing-cards.service';
import { untracked as mapUntracked } from '@angular/core';
import { createMapViewScroller } from '../../shared/map-view-scroll';
import {
  afterNextRender,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  input,
  computed as mapComputed,
  effect as mapEffect,
  signal as mapSignal,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { MapParameters } from '../../models/map-parameters.model';
import { PolygonFilter } from '../../models/polygon-filter.model';
import { RealEstateSearchResult } from '../../models/real-estate.model';
import { MapResultComponent } from '../../shared/components/map-result/map-result.component';
import { Statuses } from '../../shared/constants/status.constants';
import * as paginationActions from '../../store/actions/real-estate.actions';
import {
  changeRealEstatePage,
  changeRealEstateSort,
  clearRealEstatePolygon,
  retryRealEstateSearch,
  setRealEstateMapView,
  setRealEstatePolygon,
} from '../../store/actions/real-estate.actions';
import { selectIsArabic } from '../../store/selectors/language.selectors';
import {
  selectIsRealEstateMapView,
  selectRealEstateMapError,
  selectRealEstateMapListItems,
  selectRealEstateMapLoading,
  selectRealEstateMapSearchResults,
  selectRealEstatePagination,
  selectRealEstatePolygon,
  selectRealEstatePolygonRevision,
  selectRealEstateSearchEmpty,
  selectRealEstateSearchError,
  selectRealEstateSearchLoading,
  selectRealEstateSearchResults,
  selectRealEstateSort,
} from '../../store/selectors/real-estate.selectors';

type SortableProperty = 'createdAt' | 'price' | 'floorArea' | 'landArea' | 'yearBuilt';

@Component({
  providers: [MapListingCards],
  selector: 'app-property-search-result',
  standalone: false,
  templateUrl: './property-search-result.component.html',
  styleUrl: './property-search-result.component.scss',
})
export class PropertySearchResultComponent implements AfterViewInit, OnDestroy {
  readonly searchHistoryState = historyInject(Store).selectSignal(selectHistoryState);

  listId = input<string>('propertyList');

  @Output() mapChanged = new EventEmitter<MapParameters>();
  @Output() onListView = new EventEmitter<void>();

  @ViewChild('listTop') listTop!: ElementRef;
  @ViewChild('mapToggle') private mapToggle?: ElementRef<HTMLElement>;
  private readonly mapViewScroll = createMapViewScroller(
    () => this.mapToggle?.nativeElement,
    () => this.mapMode()
  );

  @ViewChild(MapResultComponent) propertiesMpResultComponent!: MapResultComponent;

  @Input() mapParameters?: MapParameters;
  sortProperty = '';
  sortDirection: 'asc' | 'desc' | null = 'asc';
  sortLabel = '';
  sortIcon = '';

  private readonly actions = inject(Actions);
  private readonly injector = inject(Injector);
  private pendingPage: number | null = null;
  private scrollVersion = 0;
  private router = inject(Router);
  private store = inject(Store);
  readonly mapListItems = this.store.selectSignal(selectRealEstateMapListItems);
  readonly mapCards = inject(MapListingCards);
  private readonly mapSnapshot = this.store.selectSignal(selectRealEstateMapSearchResults);
  readonly mapCardLimit = mapSignal(12);
  readonly visibleMapCards = mapComputed(() => this.mapCards.properties(this.mapListItems().slice(0, this.mapCardLimit()).map(item => item.id)));
  private readonly syncMapCards = mapEffect(() => {
    const items = this.mapSnapshot()?.items;
    const ids = this.mapListItems().slice(0, this.mapCardLimit()).map(item => item.id);
    const active = this.mapMode();
    mapUntracked(() => this.mapCards.bind('home', items, ids, active));
  });
  private readonly mapCardOrder = mapComputed(() =>
    this.mapListItems()
      .map((item) => item.id)
      .join(',')
  );
  private readonly resetMapBatch = mapEffect(() => {
    this.mapCardOrder();
    this.mapCardLimit.set(12);
  });
  loadMoreMapCards(): void {
    this.mapCardLimit.update((count) => count + 12);
  }

  readonly mapLoading$ = this.store.select(selectRealEstateMapLoading);
  readonly mapError$ = this.store.select(selectRealEstateMapError);
  private readonly mapMode = this.store.selectSignal(selectIsRealEstateMapView);
  private mapTabListener = () => this.onMapTabActivated();
  private mapTabElement?: HTMLElement;
  private timers = new Set<ReturnType<typeof setTimeout>>();
  polygon$ = this.store.select(selectRealEstatePolygon);
  polygonRevision$ = this.store.select(selectRealEstatePolygonRevision);
  error$ = this.store.select(selectRealEstateSearchError);
  loading$ = this.store.select(selectRealEstateSearchLoading);
  searchEmpty$ = this.store.select(selectRealEstateSearchEmpty);
  applyPolygon(polygon: PolygonFilter | null): void {
    this.mapParameters = undefined;
    this.onListView.emit();
    this.store.dispatch(polygon ? setRealEstatePolygon({ polygon }) : clearRealEstatePolygon());
  }
  retrySearch(): void {
    this.store.dispatch(retryRealEstateSearch());
  }

  isArabic$: Observable<boolean> = this.store.select(selectIsArabic);
  realEstateResults$ = this.store.select(selectRealEstateSearchResults);
  realEstateMapResults$ = this.store.select(selectRealEstateMapSearchResults);
  pagination$ = this.store.select(selectRealEstatePagination);
  realEstateState$ = this.store.select(selectRealEstateSort);
  isMapView$ = this.store.select(selectIsRealEstateMapView);

  visiblePages$: Observable<(number | string)[]> = this.realEstateResults$.pipe(
    map((result) => {
      if (!result) {
        return [];
      }

      const total = result.pages.length;
      const current = result.pageIndex + 1;

      if (total <= 8) {
        return result.pages;
      }

      // Beginning
      if (current <= 3) {
        return [1, 2, 3, 4, '...', total];
      }

      // End
      if (current >= total - 2) {
        return [1, '...', total - 3, total - 2, total - 1, total];
      }

      // Middle
      return [1, '...', current - 1, current, current + 1, '...', total];
    })
  );

  statuses = Statuses;

  private destroy$ = new Subject<void>();

  constructor() {
    this.actions.pipe(takeUntil(this.destroy$)).subscribe((action) => {
      if (action.type === paginationActions.searchRealEstatesSuccess.type) {
        const response = (action as ReturnType<typeof paginationActions.searchRealEstatesSuccess>)
          .propertyResults;
        if (
          this.pendingPage !== null &&
          response?.pageIndex === this.pendingPage &&
          !this.mapMode()
        ) {
          this.pendingPage = null;
          const version = this.scrollVersion;
          afterNextRender(
            () => {
              if (version !== this.scrollVersion || this.mapMode()) return;
              const anchor = this.listTop?.nativeElement as HTMLElement | undefined;
              if (!anchor) return;
              const header = document.querySelector('header.fixed-top');
              anchor.style.scrollMarginTop =
                (header?.getBoundingClientRect().height ?? 0) + 16 + 'px';
              anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
            },
            { injector: this.injector }
          );
        }
      } else if (
        (
          [
            paginationActions.searchRealEstates.type,
            paginationActions.searchMapRealEstates.type,
            paginationActions.searchRealEstatesFailure.type,
            paginationActions.setRealEstateMapView.type,
            paginationActions.setRealEstatePolygon.type,
            paginationActions.clearRealEstatePolygon.type,
            paginationActions.resetRealEstateSearch.type,
            paginationActions.retryRealEstateSearch.type,
            paginationActions.ensureRealEstateList.type,
            paginationActions.changeRealEstatePageSize.type,
            '[Auth] Logout',
            '[Auth] Clear Session State',
          ] as string[]
        ).includes(action.type)
      ) {
        this.pendingPage = null;
        this.scrollVersion++;
      }
    });
    this.realEstateState$.pipe(takeUntil(this.destroy$)).subscribe((realEstateSort) => {
      this.sortDirection = realEstateSort.direction;
      this.sortProperty = realEstateSort.active as string;
      this.setSortIcon();
      switch (realEstateSort.active) {
        case 'createdAt':
          this.sortLabel = 'ORDER_BY_DATE';
          break;
        case 'price':
          this.sortLabel = 'ORDER_BY_PRICE';
          break;
        case 'floorArea':
          this.sortLabel = 'ORDER_BY_FLOOR_A';
          break;
        case 'landArea':
          this.sortLabel = 'ORDER_BY_LAND_A';
          break;
        case 'yearBuilt':
          this.sortLabel = 'ORDER_BY_BUILD_YEAR';
          break;
        default:
          this.sortLabel = 'ORDER_BY';
      }
    });
  }

  ngAfterViewInit(): void {
    const mapTabEl = document.getElementById('map-tab');

    if (mapTabEl) {
      this.mapTabElement = mapTabEl;
      mapTabEl.addEventListener('shown.bs.tab', this.mapTabListener);
    }
  }

  setMapView(isMap: boolean): void {
    const enteringMap = isMap && !this.mapMode();
    this.mapViewScroll.cancel();
    this.store.dispatch(setRealEstateMapView({ isRealEstateMapView: isMap }));

    if (isMap) this.onMapTabActivated();
    if (enteringMap) this.mapViewScroll.request();
  }

  onMapTabActivated(): void {
    this.propertiesMpResultComponent?.invalidateMap();
  }

  updateMap(event: MapParameters) {
    this.mapChanged.emit(event);
  }

  setSortIcon(): void {
    if (!this.sortProperty) {
      this.sortIcon = 'bi-filter-square-fill';
    }

    this.sortIcon = this.sortDirection === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
  }

  goToDetails(propertyId: number): void {
    this.router.navigate(['/properties', propertyId]);
  }

  setSort(property: SortableProperty): void {
    const direction = this.getDefaultSortingDirection(property);
    this.store.dispatch(changeRealEstateSort({ active: property, direction: direction }));
  }

  getDefaultSortingDirection(property: SortableProperty): 'asc' | 'desc' {
    if (property === this.sortProperty) {
      const direction = this.sortDirection === 'asc' ? 'desc' : 'asc';

      return direction;
    }
    return 'asc';
  }

  goTo(pageIndex: number): void {
    this.requestPage(pageIndex - 1);
  }

  first(): void {
    this.requestPage(0);
  }

  last(total: number, pageSize: number): void {
    const lastPage = Math.ceil(total / pageSize) - 1;
    this.requestPage(lastPage);
  }

  isLastPage(realEstateResult: RealEstateSearchResult): boolean {
    return realEstateResult.pageIndex >= realEstateResult.pages.length - 1;
  }

  private requestPage(pageIndex: number): void {
    this.pendingPage = this.mapMode() ? null : pageIndex;
    this.scrollVersion++;
    this.store.dispatch(changeRealEstatePage({ pageIndex }));
  }

  ngOnDestroy() {
    this.scrollVersion++;
    this.mapTabElement?.removeEventListener('shown.bs.tab', this.mapTabListener);
    this.timers.forEach(clearTimeout);
    this.timers.clear();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
